import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { SIDEBAR_GRADIENT } from "./constants";
import type { ShellBackLink, ShellNavItem, ShellNavSection } from "./types";

interface ShellSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  sections: ShellNavSection[];
  header: ReactNode;
  backLink?: ShellBackLink;
  footer?: ReactNode;
  expandedWidth?: number;
  collapsedWidth?: number;
  /** `black` for admin console; default sapphire gradient. */
  tone?: "gradient" | "black";
  isItemActive?: (pathname: string, item: ShellNavItem) => boolean;
  renderItemBadge?: (item: ShellNavItem) => ReactNode;
}

function defaultIsItemActive(pathname: string, item: ShellNavItem): boolean {
  if (item.href === "/") {
    return pathname === "/";
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

export function ShellSidebar({
  collapsed,
  onToggle,
  sections,
  header,
  backLink,
  footer,
  expandedWidth = 260,
  collapsedWidth = 72,
  tone = "gradient",
  isItemActive = defaultIsItemActive,
  renderItemBadge,
}: ShellSidebarProps) {
  const location = useLocation();
  const isBlack = tone === "black";
  const allItems = sections.flatMap((section) => section.items);

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
      transition={{ duration: 0.28, ease: "easeInOut" }}
      className={cn(
        "relative z-50 flex h-screen max-h-screen shrink-0 flex-col overflow-hidden border-r shadow-2xl",
        isBlack ? "border-white/10 bg-black" : "border-white/10",
      )}
      style={isBlack ? undefined : { background: SIDEBAR_GRADIENT }}
    >
      <div className="flex min-h-[4.5rem] shrink-0 items-center border-b border-white/10 px-3 py-3">
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">{header}</AnimatePresence>
        </div>
      </div>

      {backLink && (
        <div className="shrink-0 px-3 pt-3 pb-1">
          <button
            type="button"
            onClick={backLink.onClick}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-white/60 transition-all duration-150 hover:bg-white/10 hover:text-white",
              collapsed && "justify-center px-0",
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  className="text-[12.5px] font-semibold whitespace-nowrap"
                >
                  {backLink.label}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      )}

      <nav
        className={cn(
          "min-h-0 flex-1 overflow-x-hidden overflow-y-auto py-2 overscroll-y-contain",
          isBlack &&
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30",
        )}
      >
        {sections.map((section, si) => (
          <div key={section.label} className={si > 0 ? "mt-1 border-t border-white/8 pt-1" : ""}>
            {!collapsed && (
              <p
                className={cn(
                  "px-5 pt-3 pb-1 text-[8px] font-black uppercase tracking-[0.3em]",
                  isBlack ? "text-white/55" : "text-white/25",
                )}
              >
                {section.label}
              </p>
            )}
            {section.items.map((item) => {
              // A parent link (e.g. /organizations) stays unlit while a more
              // specific sibling (e.g. /organizations/plans) matches.
              const active =
                isItemActive(location.pathname, item) &&
                !allItems.some(
                  (other) =>
                    other.href.length > item.href.length &&
                    other.href.startsWith(`${item.href}/`) &&
                    isItemActive(location.pathname, other),
                );
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "group relative mx-2 flex items-center rounded-xl px-3 py-2.5 transition-all duration-150",
                    collapsed ? "justify-center" : "gap-3",
                    active
                      ? "bg-white/20 text-white shadow-lg shadow-black/10"
                      : isBlack
                        ? "text-white/90 hover:bg-white/10 hover:text-white"
                        : "text-white/55 hover:bg-white/10 hover:text-white",
                  )}
                >
                  {active && !collapsed && (
                    <span className="absolute left-1.5 h-1.5 w-1.5 rounded-full bg-sky-300" />
                  )}
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  <AnimatePresence>
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.15 }}
                        className="text-[13px] font-semibold whitespace-nowrap"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {renderItemBadge?.(item)}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {footer}

      <div className={cn("shrink-0 border-t border-white/10 p-3", isBlack ? "bg-black" : "bg-black/10")}>
        <div className={cn("flex items-center", collapsed ? "justify-center" : "justify-between gap-2")}>
          <button
            type="button"
            onClick={onToggle}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </motion.aside>
  );
}
